/** Validation history (FR-18): past runs, re-downloadable reports. */

import { useCallback, useEffect, useState } from 'react';
import * as api from '../lib/api';
import { ApiError } from '../lib/api';
import type { AppConfig, HistoryPage, ValidationReport } from '../lib/types';
import { ReportView } from './ReportView';
import {
  Callout,
  DownloadIcon,
  InboxIcon,
  Spinner,
  StatusBadge,
  formatBytes,
  formatDateTime,
  formatDuration,
} from './primitives';

const PAGE_SIZE = 20;

export function HistoryView({ config }: { config: AppConfig }) {
  const [page, setPage] = useState<HistoryPage | null>(null);
  const [offset, setOffset] = useState(0);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<ValidationReport | null>(null);
  // Which run's share link is currently displayed for copying, keyed by run
  // id -- separate from `page`'s own data since revealing/revoking a link
  // updates this without needing a full history reload. Empty for a run
  // that's shared but whose link hasn't been revealed in *this* session yet
  // (e.g. shared earlier, page reloaded since) -- "Share"/"Copy link" always
  // re-fetches it (the backend call is idempotent, same token comes back)
  // rather than assuming it's already known.
  const [shareLinks, setShareLinks] = useState<Record<string, string>>({});
  const [sharing, setSharing] = useState<string | null>(null);

  function setRunShared(runId: string, shared: boolean) {
    setPage((current) =>
      current
        ? { ...current, runs: current.runs.map((run) => (run.id === runId ? { ...run, shared } : run)) }
        : current,
    );
  }

  async function revealShareLink(runId: string) {
    setSharing(runId);
    setError(null);
    try {
      const { share_token } = await api.shareRun(runId);
      setShareLinks((links) => ({ ...links, [runId]: `${window.location.origin}/shared/${share_token}` }));
      setRunShared(runId, true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not create a share link.');
    } finally {
      setSharing(null);
    }
  }

  async function revokeShareLink(runId: string) {
    setSharing(runId);
    setError(null);
    try {
      await api.unshareRun(runId);
      setShareLinks((links) => {
        const next = { ...links };
        delete next[runId];
        return next;
      });
      setRunShared(runId, false);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not revoke that share link.');
    } finally {
      setSharing(null);
    }
  }

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setPage(await api.getHistory(PAGE_SIZE, offset));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not load history.');
    } finally {
      setBusy(false);
    }
  }, [offset]);

  useEffect(() => {
    void load();
  }, [load]);

  if (open) {
    return (
      <div className="stack">
        <button type="button" className="btn" onClick={() => setOpen(null)}>
          ← Back to history
        </button>
        <ReportView report={open} watermarked={config.watermark_reports} />
      </div>
    );
  }

  const runs = page?.runs ?? [];

  return (
    <div className="stack">
      {!config.features.history && (
        <Callout variant="upsell">
          <strong>Validation history is a Professional feature.</strong>
          <div style={{ marginTop: '0.2rem' }}>
            Runs are kept for {config.history_retention_days} days so you can re-issue a report
            without re-uploading the model.
          </div>
        </Callout>
      )}

      <div className="card">
        <div className="card-head">
          <h2>Validation history</h2>
          <div className="masthead-spacer" />
          {page && (
            <span className="hint">
              {page.total.toLocaleString()} run(s) · kept {page.retention_days} days
            </span>
          )}
          <button type="button" className="btn btn-sm" onClick={() => void load()} disabled={busy}>
            Refresh
          </button>
        </div>

        {error && (
          <div className="card-body tight">
            <Callout variant="error">{error}</Callout>
          </div>
        )}

        {busy && (
          <div className="card-body tight row">
            <Spinner /> <span className="hint-strong">Loading…</span>
          </div>
        )}

        {!busy && runs.length === 0 ? (
          <div className="empty">
            <InboxIcon />
            <p>No validations recorded yet.</p>
            <p className="hint">Runs appear here once you have validated a file.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="history-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Result</th>
                  <th>Findings</th>
                  <th>Rule set</th>
                  <th>Schema</th>
                  <th>Size</th>
                  <th>Time</th>
                  <th>When</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id}>
                    <td>{run.filename}</td>
                    <td>
                      <StatusBadge
                        status={
                          run.verdict === 'pass'
                            ? 'pass'
                            : run.verdict === 'pass_with_warnings'
                              ? 'warning'
                              : 'fail'
                        }
                      />
                    </td>
                    <td>{run.total_findings.toLocaleString()}</td>
                    <td className="hint">
                      {run.template_name}
                      {run.ids_filename ? ` + ${run.ids_filename}` : ''}
                    </td>
                    <td className="cell-mono">{run.schema || '—'}</td>
                    <td className="cell-mono">{formatBytes(run.size_bytes)}</td>
                    <td className="cell-mono">{formatDuration(run.duration_ms)}</td>
                    <td className="cell-mono">{formatDateTime(run.created_at)}</td>
                    <td>
                      <div className="row row-nowrap">
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={async () => {
                            try {
                              const body = await api.getRun(run.id);
                              setOpen(body.report);
                            } catch (caught) {
                              setError(
                                caught instanceof ApiError
                                  ? caught.message
                                  : 'Could not open that run.',
                              );
                            }
                          }}
                        >
                          View
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => api.downloadRunReport(run.id, 'pdf')}
                          aria-label={`Download PDF for ${run.filename}`}
                        >
                          <DownloadIcon /> PDF
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => api.downloadRunReport(run.id, 'csv')}
                          aria-label={`Download CSV for ${run.filename}`}
                        >
                          CSV
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => api.downloadRunReport(run.id, 'bcf')}
                          aria-label={`Download BCF issues for ${run.filename}`}
                          title="One BCF issue per failed check, openable directly in a coordination tool."
                        >
                          BCF
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm"
                          disabled={sharing === run.id}
                          onClick={() => void revealShareLink(run.id)}
                          aria-label={
                            run.shared
                              ? `Show the shared link for ${run.filename}`
                              : `Get a shareable link for ${run.filename}`
                          }
                        >
                          {run.shared ? 'Copy link' : 'Share'}
                        </button>
                      </div>
                      {shareLinks[run.id] && (
                        <div className="row" style={{ marginTop: '0.4rem' }}>
                          <input
                            type="text"
                            readOnly
                            value={shareLinks[run.id]}
                            className="cell-mono"
                            style={{ flex: 1, minWidth: '16rem' }}
                            onFocus={(event) => event.currentTarget.select()}
                          />
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => void navigator.clipboard.writeText(shareLinks[run.id]!)}
                          >
                            Copy
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm"
                            disabled={sharing === run.id}
                            onClick={() => void revokeShareLink(run.id)}
                            aria-label={`Revoke the shared link for ${run.filename}`}
                          >
                            Revoke
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {page && page.total > PAGE_SIZE && (
          <div className="card-body tight row">
            <button
              type="button"
              className="btn btn-sm"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              ← Newer
            </button>
            <span className="hint">
              {offset + 1}–{Math.min(offset + PAGE_SIZE, page.total)} of {page.total}
            </span>
            <button
              type="button"
              className="btn btn-sm"
              disabled={offset + PAGE_SIZE >= page.total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Older →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
